import { morphSection } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';

if (!window.__amadaCartLineBound) {
  window.__amadaCartLineBound = true;

  const FEE_HANDLE = 'amada-hand-engraving';
  const UNKNOWN_SIZE = "I don't know";
  const CART_PAUSE_MS = 600;

  /** @type {Promise<any> | null} */
  let feeSyncPromise = null;
  /** One cart mutation at a time so /cart/change.js never overlaps. */
  let cartQueue = Promise.resolve();

  function pause(ms = CART_PAUSE_MS) {
    return new Promise((resolve) => {
      window.setTimeout(resolve, ms);
    });
  }

  function enqueueCart(task) {
    const run = cartQueue.then(task, task);
    cartQueue = run.then(
      () => undefined,
      () => undefined
    );
    return run;
  }

  /** The market's cart path (/en-us/cart), so cart calls keep the visitor's market and currency. */
  function cartRoot() {
    return (typeof Theme !== 'undefined' && Theme.routes && Theme.routes.cart_url) || '/cart';
  }

  async function readCart() {
    const response = await fetch(cartUrl(), {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
    });
    if (!response.ok) throw new Error(`Failed to read cart: ${response.status}`);
    return response.json();
  }

  function cartChangeUrl() {
    return `${cartRoot()}/change.js`;
  }

  function cartAddUrl() {
    return `${cartRoot()}/add.js`;
  }

  function cartUrl() {
    return `${cartRoot()}.js`;
  }

  function sectionId() {
    const cartItems = document.querySelector('cart-items-component');
    return cartItems instanceof HTMLElement ? cartItems.dataset.sectionId : '';
  }

  /**
   * Shopify Liquid `item.properties | json` can serialize as an Array of pairs.
   * /cart/change.js requires a Hash — normalize anything we read from the DOM.
   * @param {unknown} raw
   * @returns {Record<string, string>}
   */
  function normalizeProperties(raw) {
    /** @type {Record<string, string>} */
    const out = {};
    if (!raw) return out;

    if (Array.isArray(raw)) {
      for (const entry of raw) {
        if (Array.isArray(entry) && entry.length >= 2) {
          const key = String(entry[0] ?? '').trim();
          if (!key) continue;
          out[key] = entry[1] == null ? '' : String(entry[1]);
        } else if (entry && typeof entry === 'object') {
          if ('first' in entry || 'name' in entry || 'key' in entry) {
            const key = String(entry.first ?? entry.name ?? entry.key ?? '').trim();
            if (!key) continue;
            out[key] = entry.last ?? entry.value ?? '';
            out[key] = out[key] == null ? '' : String(out[key]);
          } else {
            for (const [k, v] of Object.entries(entry)) {
              if (!k) continue;
              out[k] = v == null ? '' : String(v);
            }
          }
        }
      }
      return out;
    }

    if (typeof raw === 'object') {
      for (const [k, v] of Object.entries(/** @type {Record<string, unknown>} */ (raw))) {
        if (!k || k === 'length') continue;
        // Skip array index keys if a weird hybrid slipped through
        if (/^\d+$/.test(k) && (Array.isArray(v) || (v && typeof v === 'object'))) continue;
        out[k] = v == null ? '' : String(v);
      }
    }

    return out;
  }

  function readProperties(control) {
    try {
      return normalizeProperties(JSON.parse(control.dataset.properties || '{}'));
    } catch {
      return {};
    }
  }

  /**
   * Read engraving text from cart line properties (any common key shape).
   * @param {unknown} rawProps
   * @returns {string}
   */
  function lineEngravingText(rawProps) {
    const props = normalizeProperties(rawProps);
    const direct =
      props.Engraving ||
      props.engraving ||
      props['Engraving text'] ||
      props['engraving text'] ||
      '';
    if (String(direct).trim()) return String(direct).trim();

    for (const [key, value] of Object.entries(props)) {
      const k = String(key).toLowerCase().trim();
      if (k === 'engraving' || k === 'engraving text' || k.includes('engrav')) {
        const text = String(value || '').trim();
        if (text) return text;
      }
    }
    return '';
  }

  function isFeeItem(item) {
    return item?.handle === FEE_HANDLE;
  }

  function feeOwnerId(item) {
    const props = normalizeProperties(item?.properties);
    return String(props._for || '').trim();
  }

  function engravedNeedByVariant(cart) {
    /** @type {Record<string, number>} */
    const need = {};
    for (const item of cart?.items || []) {
      if (isFeeItem(item)) continue;
      if (!lineEngravingText(item.properties)) continue;
      const vid = String(item.variant_id);
      need[vid] = (need[vid] || 0) + Number(item.quantity || 1);
    }
    return need;
  }

  /** @type {number | string | null} */
  let cachedFeeVariantId = null;

  async function getFeeVariantId(cart) {
    if (cachedFeeVariantId) return cachedFeeVariantId;
    const existing = (cart?.items || []).find(isFeeItem);
    if (existing?.variant_id) {
      cachedFeeVariantId = existing.variant_id;
      return cachedFeeVariantId;
    }
    try {
      const feeProduct = await (
        await fetch('/products/amada-hand-engraving.js', { headers: { Accept: 'application/json' } })
      ).json();
      cachedFeeVariantId = feeProduct?.variants?.[0]?.id || null;
    } catch {
      cachedFeeVariantId = null;
    }
    return cachedFeeVariantId;
  }

  /**
   * Keep one £50 fee line per engraved product (`properties._for` = variant id).
   * Never pool total engraved qty onto the first fee line — that added extra £50.
   * @param {any} cart
   */
  async function reconcileEngravingFees(cart) {
    let lastData = null;
    let live = cart || (await readCart());
    const need = engravedNeedByVariant(live);
    const feeItems = (live.items || []).filter(isFeeItem);

    /** @type {Record<string, any[]>} */
    const byOwner = {};
    const unlinked = [];
    for (const fee of feeItems) {
      const owner = feeOwnerId(fee);
      if (owner) (byOwner[owner] ||= []).push(fee);
      else unlinked.push(fee);
    }

    const usedUnlinked = new Set();
    const owners = new Set([...Object.keys(need), ...Object.keys(byOwner)]);

    for (const owner of owners) {
      const want = Number(need[owner] || 0);
      const lines = byOwner[owner] || [];

      if (want === 0) {
        for (const line of lines) {
          lastData = await cartRequest(cartChangeUrl(), sectionRequestBody({ id: line.key, quantity: 0 }));
          await pause();
        }
        continue;
      }

      if (!lines.length) {
        const spare = unlinked.find((line) => !usedUnlinked.has(line.key));
        if (spare) {
          usedUnlinked.add(spare.key);
          lastData = await cartRequest(
            cartChangeUrl(),
            sectionRequestBody({
              id: spare.key,
              quantity: want,
              properties: { _for: owner },
            })
          );
          await pause();
          continue;
        }
        const feeVid = await getFeeVariantId(live);
        if (!feeVid) continue;
        lastData = await cartRequest(
          cartAddUrl(),
          sectionRequestBody({
            id: feeVid,
            quantity: want,
            properties: { _for: owner },
          })
        );
        await pause();
        continue;
      }

      const primary = lines[0];
      if (Number(primary.quantity) !== want) {
        lastData = await cartRequest(
          cartChangeUrl(),
          sectionRequestBody({
            id: primary.key,
            quantity: want,
            properties: { _for: owner },
          })
        );
        await pause();
      }
      for (const extra of lines.slice(1)) {
        lastData = await cartRequest(cartChangeUrl(), sectionRequestBody({ id: extra.key, quantity: 0 }));
        await pause();
      }
    }

    for (const spare of unlinked) {
      if (usedUnlinked.has(spare.key)) continue;
      lastData = await cartRequest(cartChangeUrl(), sectionRequestBody({ id: spare.key, quantity: 0 }));
      await pause();
    }

    live = await readCart();
    return { lastData, cart: live };
  }

  /**
   * Updates a cart line property by Shopify line key so checkout sees the new value.
   * @param {HTMLElement} control
   * @param {string} value
   * @param {{ removeWhenBlank?: boolean, skipReload?: boolean, skipMorph?: boolean }} [options]
   */
  async function updateLineProperty(control, value, options = {}) {
    if (control.dataset.updating === 'true') return;

    const lineKey = control.dataset.lineKey;
    const quantity = Number(control.dataset.quantity || 1);
    const propertyKey = control.dataset.propertyKey || '';
    if (!lineKey || !propertyKey) return;

    const properties = readProperties(control);
    const next = String(value || '').trim();
    const current = String(properties[propertyKey] || '').trim();
    if (current === next) return;

    if (next) {
      properties[propertyKey] = next;
    } else if (options.removeWhenBlank) {
      delete properties[propertyKey];
    } else {
      return;
    }

    // Guarantee a plain object (never an Array) for Shopify's schema check
    const propertiesHash = normalizeProperties(properties);

    const id = sectionId();
    control.dataset.updating = 'true';
    if ('disabled' in control) control.disabled = true;

    try {
      const body = {
        id: lineKey,
        quantity,
        properties: propertiesHash,
      };
      if (id) {
        body.sections = id;
        body.sections_url = window.location.pathname;
      }

      const response = await fetch(cartChangeUrl(), fetchConfig('json', { body: JSON.stringify(body) }));
      const data = await response.json();
      if (!response.ok || data.errors || data.status) {
        console.warn('[amada-cart] Line property update failed:', data.errors || data.description || data.status);
        return;
      }

      const isEngraving =
        propertyKey.toLowerCase() === 'engraving' || propertyKey.toLowerCase().includes('engrav');

      if (isEngraving) {
        // Product-remove flow clears engraving first, then removes the line — no UI update here.
        if (options.skipReload || options.skipMorph) {
          await syncEngravingFee(data, { skipMorph: true });
          return data;
        }

        const synced = await syncEngravingFee(data, { skipMorph: false });
        const morphSource = synced || data;
        if (id && morphSource?.sections && morphSource.sections[id]) {
          await morphSection(id, morphSource.sections[id], { mode: 'full' });
          hydrateEngravingUi();
          return data;
        }
        if (id && data.sections && data.sections[id]) {
          await morphSection(id, data.sections[id], { mode: 'full' });
          hydrateEngravingUi();
          return data;
        }
        // Last resort only when section HTML is missing — never loop on empty bag.
        if (!document.querySelector('[data-amada-cart-empty]') && document.querySelector('.amada-cart .cart-items__table-row')) {
          window.location.reload();
        }
        return data;
      }

      if (options.skipReload) return data;

      if (id && data.sections && data.sections[id]) {
        await morphSection(id, data.sections[id], { mode: 'full' });
      } else if (!document.querySelector('[data-amada-cart-empty]')) {
        window.location.reload();
      }
      return data;
    } catch (error) {
      console.warn('[amada-cart] Line property update failed:', error);
    } finally {
      if (control.isConnected) {
        control.dataset.updating = 'false';
        if ('disabled' in control) control.disabled = false;
      }
    }
  }

  /**
   * Full-cart delete sequence. Never fires a second /cart/change.js until we
   * pause and re-read /cart.js so line keys are fresh.
   *
   * 1) GET /cart.js
   * 2) If engraved: POST /cart/change.js on the fee line
   * 3) pause → GET /cart.js
   * 4) POST /cart/change.js to remove the product with the fresh key
   * 5) pause → GET /cart.js
   * 6) If an orphan fee remains: POST /cart/change.js with the new fee key
   *
   * @param {string} lineKey
   * @param {HTMLElement} [row]
   * @returns {Promise<{ lineKey: string, removed: boolean, lastData: any }>}
   */
  async function removeCartLineSequenced(lineKey, row) {
    return enqueueCart(async () => {
      const variantId =
        row instanceof HTMLElement
          ? row.getAttribute('data-variant-id') ||
            row.querySelector('[data-variant-id]')?.getAttribute('data-variant-id') ||
            ''
          : '';

      const findProduct = (items, preferredKey) =>
        (items || []).find((item) => item.key === preferredKey && item.handle !== FEE_HANDLE) ||
        (items || []).find(
          (item) =>
            item.handle !== FEE_HANDLE &&
            variantId &&
            String(item.variant_id) === String(variantId)
        );

      let cart = await readCart();
      let product = findProduct(cart.items, lineKey);
      if (!product) return { lineKey, removed: false, lastData: null };

      let lastData = null;
      const eng = lineEngravingText(product.properties);
      if (eng) {
        const owner = String(product.variant_id);
        const ownerFees = (cart.items || []).filter((item) => isFeeItem(item) && feeOwnerId(item) === owner);
        const leftoverFees = ownerFees.length
          ? ownerFees
          : (cart.items || []).filter((item) => isFeeItem(item) && !feeOwnerId(item));
        for (const fee of leftoverFees) {
          lastData = await cartRequest(cartChangeUrl(), sectionRequestBody({ id: fee.key, quantity: 0 }));
          await pause();
        }
        cart = await readCart();
        product = findProduct(cart.items, product.key) || findProduct(cart.items, lineKey);
        if (!product) {
          const reconciled = await reconcileEngravingFees(cart);
          lastData = reconciled.lastData || lastData;
          cart = reconciled.cart;
          updateCartBubble(visibleCartCount(cart));
          if (lastData) await morphFromResponse(lastData);
          return { lineKey, removed: true, lastData };
        }
      }

      lastData = await cartRequest(
        cartChangeUrl(),
        sectionRequestBody({ id: product.key, quantity: 0 })
      );

      await pause();
      cart = await readCart();

      const reconciled = await reconcileEngravingFees(cart);
      lastData = reconciled.lastData || lastData;
      cart = reconciled.cart;

      if (Number(cart.item_count || 0) === 0) {
        document.querySelectorAll('style[data-amada-empty-cart-hide]').forEach((node) => node.remove());
      }
      updateCartBubble(visibleCartCount(cart));
      if (lastData) await morphFromResponse(lastData);

      return { lineKey: product.key, removed: true, lastData };
    });
  }

  window.__amadaRemoveCartLineSequenced = removeCartLineSequenced;

  /**
   * Quantity change for an engraved product: product qty first, pause, then
   * fee qty. One morph at the end so SUMMARY never flashes a stale engraving total.
   * @param {string} lineKey
   * @param {number} quantity
   * @param {HTMLElement} [row]
   */
  async function changeCartLineQuantitySequenced(lineKey, quantity, row) {
    return enqueueCart(async () => {
      const nextQty = Math.max(0, Number(quantity) || 0);
      if (nextQty === 0) {
        return removeCartLineSequenced(lineKey, row);
      }

      const variantId =
        row instanceof HTMLElement
          ? row.getAttribute('data-variant-id') ||
            row.querySelector('[data-variant-id]')?.getAttribute('data-variant-id') ||
            ''
          : '';

      const findProduct = (items, preferredKey) =>
        (items || []).find((item) => item.key === preferredKey && item.handle !== FEE_HANDLE) ||
        (items || []).find(
          (item) =>
            item.handle !== FEE_HANDLE &&
            variantId &&
            String(item.variant_id) === String(variantId)
        );

      let cart = await readCart();
      let product = findProduct(cart.items, lineKey);
      if (!product) return { lineKey, lastData: null };

      let lastData = await cartRequest(
        cartChangeUrl(),
        sectionRequestBody({ id: product.key, quantity: nextQty })
      );

      await pause();
      cart = await readCart();

      const reconciled = await reconcileEngravingFees(cart);
      lastData = reconciled.lastData || lastData;
      cart = reconciled.cart;

      updateCartBubble(visibleCartCount(cart));
      if (lastData) await morphFromResponse(lastData);
      return { lineKey: product.key, lastData };
    });
  }

  window.__amadaChangeCartLineQuantitySequenced = changeCartLineQuantitySequenced;

  /**
   * @deprecated Use removeCartLineSequenced. Kept so older callers do not throw.
   * @param {string} lineKey
   * @param {HTMLElement} [row]
   */
  async function clearLineEngravingBeforeRemove(lineKey, row) {
    return { removed: false, lineKey };
  }

  window.__amadaClearLineEngravingBeforeRemove = clearLineEngravingBeforeRemove;

  function sectionRequestBody(extra = {}) {
    const id = sectionId();
    const body = { ...extra };
    if (id) {
      body.sections = id;
      body.sections_url = window.location.pathname;
    }
    return body;
  }

  async function morphFromResponse(data) {
    const id = sectionId();
    if (id && data?.sections && data.sections[id]) {
      await morphSection(id, data.sections[id], { mode: 'full' });
      if (!document.querySelector('.amada-cart .cart-items__table-row:not([data-amada-engraving-fee-line])')) {
        document.querySelectorAll('style[data-amada-empty-cart-hide]').forEach((node) => node.remove());
      }
      return true;
    }
    return false;
  }

  async function cartRequest(url, body) {
    const response = await fetch(url, fetchConfig('json', { body: JSON.stringify(body) }));
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.errors || data.status) {
      // Removing a line that is already gone is not fatal (stale key after a prior change).
      if (Number(body.quantity) === 0) return data;
      throw new Error(data.errors || data.description || data.message || 'Cart request failed');
    }
    return data;
  }

  /**
   * True when the cart only has engraving fee lines left (no real products).
   * @param {{ items?: Array<{ handle?: string }> }} cart
   */
  function visibleCartCount(cart) {
    return (cart?.items || []).reduce((sum, item) => {
      if (item.handle === FEE_HANDLE) return sum;
      return sum + Number(item.quantity || 0);
    }, 0);
  }

  function updateCartBubble(count) {
    const n = Math.max(0, Number(count || 0));
    const empty = n === 0;
    const label = n > 99 ? '99+' : String(n);
    document.querySelectorAll('[data-testid="cart-bubble"], .cart-bubble__text-count').forEach((el) => {
      el.textContent = empty ? '' : label;
      el.classList.toggle('hidden', empty);
    });
    document.querySelectorAll('.cart-bubble').forEach((bubble) => {
      bubble.classList.toggle('visually-hidden', empty);
    });
    document.querySelectorAll('cart-icon').forEach((icon) => {
      icon.classList.toggle('header-actions__cart-icon--has-cart', !empty);
    });
    try {
      sessionStorage.setItem(
        'cart-count',
        JSON.stringify({
          value: empty ? '0' : label,
          timestamp: Date.now(),
        })
      );
    } catch (_) {
      // sessionStorage may be unavailable
    }
  }

  window.__amadaUpdateCartBubble = updateCartBubble;

  function cartHasOnlyFeeLines(cart) {
    const items = cart?.items || [];
    if (!items.length) return false;
    return items.every((item) => item.handle === FEE_HANDLE);
  }

  /**
   * Keep hand-engraving fee line in sync with jewellery lines that have engraving text.
   * Removes orphan £50 fee lines so the empty bag can show.
   * Never full-page-reloads on an already-empty cart (that caused a load loop).
   * @param {any} [pendingSectionData]
   * @param {{ skipMorph?: boolean }} [options]
   */
  async function syncEngravingFee(pendingSectionData, options = {}) {
    if (feeSyncPromise) return feeSyncPromise;

    feeSyncPromise = enqueueCart(async () => {
      try {
        const cart = await readCart();
        const reconciled = await reconcileEngravingFees(cart);
        let lastData = reconciled.lastData;
        let live = reconciled.cart;
        const feeChanged = Boolean(lastData);

        const onlyFee = cartHasOnlyFeeLines(live);

        // Fee was the only thing left — clear it so empty bag can show.
        if (onlyFee) {
          for (const leftoverFee of (live.items || []).filter(isFeeItem)) {
            lastData = await cartRequest(
              cartChangeUrl(),
              sectionRequestBody({ id: leftoverFee.key, quantity: 0 })
            );
            await pause();
          }
          live = await readCart();
        }

        if (options.skipMorph) {
          return lastData || pendingSectionData || null;
        }

        // Reveal empty-cart companion sections when bag is empty after fee cleanup.
        const finalCart = feeChanged
          ? await (await fetch(cartUrl(), { headers: { Accept: 'application/json' } })).json()
          : live;
        const finalCount = Number(finalCart.item_count || 0);
        if (finalCount === 0) {
          document.querySelectorAll('style[data-amada-empty-cart-hide]').forEach((node) => node.remove());
        }

        // Prefer section morph — never reload when the empty bag is already painted.
        const pageAlreadyEmpty = Boolean(document.querySelector('[data-amada-cart-empty]'));
        const morphData = lastData || pendingSectionData;
        if (morphData && (await morphFromResponse(morphData))) {
          if (finalCount === 0) {
            document.querySelectorAll('style[data-amada-empty-cart-hide]').forEach((node) => node.remove());
          }
          return morphData;
        }

        // Nothing changed — keep current HTML (boot on a stable cart).
        if (!feeChanged) {
          return pendingSectionData || null;
        }

        // Fee changed but no section HTML: one no-op change on a remaining line to refresh sections.
        const refreshed = await (await fetch(cartUrl(), { headers: { Accept: 'application/json' } })).json();
        if (!(refreshed.items || []).length) {
          document.querySelectorAll('style[data-amada-empty-cart-hide]').forEach((node) => node.remove());
          // Already empty in the DOM — stop. Do not reload (reload loop).
          if (pageAlreadyEmpty) return lastData || pendingSectionData || null;
          // Still showing product rows after fee wipe — morph via section id if we have it,
          // otherwise a single reload is acceptable (not on every subsequent load).
          const id = sectionId();
          if (id && lastData?.sections?.[id]) {
            await morphSection(id, lastData.sections[id], { mode: 'full' });
            return lastData;
          }
          return lastData || pendingSectionData || null;
        }

        const first =
          (refreshed.items || []).find((item) => item.handle !== FEE_HANDLE) || refreshed.items[0];
        if (!first) return pendingSectionData || null;
        const refresh = await cartRequest(
          cartChangeUrl(),
          sectionRequestBody({ id: first.key, quantity: first.quantity })
        );
        await morphFromResponse(refresh);
        return refresh;
      } catch (error) {
        console.warn('[amada-cart] Engraving fee sync failed:', error);
        return pendingSectionData || null;
      } finally {
        feeSyncPromise = null;
      }
    })();

    return feeSyncPromise;
  }

  // Cart page + other scripts can await the same fee sync.
  window.__amadaSyncEngravingFee = syncEngravingFee;

  function engravingRoot(el) {
    return el instanceof Element ? el.closest('[data-amada-cart-engraving]') : null;
  }

  function showEngravingCompose(root, { focus = true } = {}) {
    if (!(root instanceof HTMLElement)) return;
    const compose = root.querySelector('[data-amada-engraving-compose]');
    const saved = root.querySelector('[data-amada-engraving-saved]');
    if (compose instanceof HTMLElement) compose.hidden = false;
    if (saved instanceof HTMLElement) saved.hidden = true;
    root.classList.remove('is-saved');
    root.dataset.mode = 'compose';
    const input = root.querySelector('input[data-amada-engraving]');
    setEngravingAddLoading(root, false);
    syncEngravingAddVisibility(root);
    if (input instanceof HTMLInputElement && focus) {
      input.focus();
      const len = input.value.length;
      try { input.setSelectionRange(len, len); } catch {}
    }
  }

  function showEngravingSaved(root, value) {
    if (!(root instanceof HTMLElement)) return;
    const compose = root.querySelector('[data-amada-engraving-compose]');
    const saved = root.querySelector('[data-amada-engraving-saved]');
    const display = root.querySelector('[data-amada-engraving-display]');
    const addBtn = root.querySelector('[data-amada-engraving-add]');
    const text = String(value || '').trim();
    if (!text) {
      showEngravingCompose(root, { focus: false });
      return;
    }
    if (compose instanceof HTMLElement) compose.hidden = true;
    if (saved instanceof HTMLElement) saved.hidden = false;
    if (display instanceof HTMLElement) display.textContent = `"${text}"`;
    if (addBtn instanceof HTMLButtonElement) {
      addBtn.hidden = true;
      addBtn.classList.remove('is-loading');
      addBtn.disabled = false;
    }
    root.classList.add('is-saved');
    root.dataset.mode = 'saved';
  }

  function setEngravingAddLoading(root, loading) {
    const addBtn = root?.querySelector?.('[data-amada-engraving-add]');
    if (!(addBtn instanceof HTMLButtonElement)) return;
    addBtn.classList.toggle('is-loading', Boolean(loading));
    addBtn.disabled = Boolean(loading);
    addBtn.setAttribute('aria-busy', loading ? 'true' : 'false');
  }


  function setEngravingRemoveLoading(root, loading) {
    const removeBtn = root?.querySelector?.('[data-amada-engraving-remove]');
    const editBtn = root?.querySelector?.('[data-amada-engraving-edit]');
    if (!(removeBtn instanceof HTMLButtonElement)) return;
    removeBtn.classList.toggle('is-loading', Boolean(loading));
    removeBtn.disabled = Boolean(loading);
    removeBtn.setAttribute('aria-busy', loading ? 'true' : 'false');
    if (editBtn instanceof HTMLButtonElement) editBtn.disabled = Boolean(loading);
    if (root instanceof HTMLElement) root.classList.toggle('is-removing', Boolean(loading));
  }

  function syncEngravingAddVisibility(root) {
    if (!(root instanceof HTMLElement)) return;
    if (root.classList.contains('is-saved') || root.dataset.mode === 'saved') return;
    const input = root.querySelector('input[data-amada-engraving]');
    const addBtn = root.querySelector('[data-amada-engraving-add]');
    if (!(addBtn instanceof HTMLButtonElement)) return;
    const hasText = Boolean(String(input?.value || '').trim());
    addBtn.hidden = !hasText;
    if (!hasText) {
      addBtn.classList.remove('is-loading');
      addBtn.disabled = false;
    }
  }

  async function submitEngraving(root) {
    if (!(root instanceof HTMLElement)) return;
    const input = root.querySelector('input[data-amada-engraving]');
    if (!(input instanceof HTMLInputElement)) return;
    const next = String(input.value || '').trim();
    if (!next) {
      syncEngravingAddVisibility(root);
      return;
    }
    setEngravingAddLoading(root, true);
    try {
      await updateLineProperty(input, next, { removeWhenBlank: true });
      // If morph did not replace the node, flip UI locally
      if (root.isConnected) {
        showEngravingSaved(root, next);
        setEngravingAddLoading(root, false);
      }
    } catch (error) {
      setEngravingAddLoading(root, false);
      console.warn('[amada-cart] Engraving add failed:', error);
    }
  }

  function isUnknownSize(value) {
    const v = String(value || '').trim().toLowerCase();
    return !v || v.includes("don't know") || v.includes('dont know') || v === 'unknown';
  }

  function showSizeEdit(root) {
    if (!(root instanceof HTMLElement)) return;
    const summary = root.querySelector('[data-amada-cart-size-summary]');
    const edit = root.querySelector('[data-amada-cart-size-edit]');
    if (summary instanceof HTMLElement) summary.hidden = true;
    if (edit instanceof HTMLElement) edit.hidden = false;
    const select = edit?.querySelector('select[data-amada-ring-size]');
    if (select instanceof HTMLSelectElement) {
      select.focus();
    }
  }

  function showSizeSummary(root, label) {
    if (!(root instanceof HTMLElement)) return;
    const summary = root.querySelector('[data-amada-cart-size-summary]');
    const edit = root.querySelector('[data-amada-cart-size-edit]');
    const valueEl = root.querySelector('[data-amada-cart-size-summary-value]');
    if (valueEl instanceof HTMLElement) valueEl.textContent = label || UNKNOWN_SIZE;
    if (summary instanceof HTMLElement) summary.hidden = false;
    if (edit instanceof HTMLElement) edit.hidden = true;
  }


  /* ---- What arrives lightbox gallery ---- */
  let arrivesLightbox = null;
  let arrivesSlides = [];
  let arrivesIndex = 0;
  let arrivesLastFocus = null;

  function ensureArrivesLightbox() {
    if (arrivesLightbox instanceof HTMLElement) return arrivesLightbox;

    const root = document.createElement('div');
    root.className = 'amada-arrives-lightbox';
    root.hidden = true;
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-label', 'What arrives with your order');
    root.innerHTML = `
      <button type="button" class="amada-arrives-lightbox__backdrop" data-amada-arrives-close aria-label="Close"></button>
      <div class="amada-arrives-lightbox__dialog">
        <div class="amada-arrives-lightbox__toolbar">
          <p class="amada-arrives-lightbox__caption" data-amada-arrives-caption></p>
          <button type="button" class="amada-arrives-lightbox__close" data-amada-arrives-close aria-label="Close">&times;</button>
        </div>
        <div class="amada-arrives-lightbox__stage">
          <button type="button" class="amada-arrives-lightbox__nav amada-arrives-lightbox__nav--prev" data-amada-arrives-prev aria-label="Previous">&#8249;</button>
          <img class="amada-arrives-lightbox__image" data-amada-arrives-image alt="">
          <button type="button" class="amada-arrives-lightbox__nav amada-arrives-lightbox__nav--next" data-amada-arrives-next aria-label="Next">&#8250;</button>
        </div>
        <div class="amada-arrives-lightbox__dots" data-amada-arrives-dots></div>
      </div>
    `;
    document.body.appendChild(root);
    arrivesLightbox = root;
    return root;
  }

  function paintArrivesSlide() {
    const root = ensureArrivesLightbox();
    const slide = arrivesSlides[arrivesIndex];
    if (!slide) return;

    const img = root.querySelector('[data-amada-arrives-image]');
    const caption = root.querySelector('[data-amada-arrives-caption]');
    const prev = root.querySelector('[data-amada-arrives-prev]');
    const next = root.querySelector('[data-amada-arrives-next]');
    const dots = root.querySelector('[data-amada-arrives-dots]');

    if (img instanceof HTMLImageElement) {
      const raw = slide.src || '';
      img.src = raw.includes('cdn.shopify.com') && !raw.includes('width=')
        ? raw + (raw.includes('?') ? '&' : '?') + 'width=1600'
        : raw.replace(/([?&])width=\d+/, '$1width=1600');

      img.alt = slide.caption || '';
    }
    if (caption instanceof HTMLElement) {
      caption.textContent = slide.caption || '';
    }
    if (prev instanceof HTMLButtonElement) {
      prev.disabled = arrivesSlides.length < 2;
    }
    if (next instanceof HTMLButtonElement) {
      next.disabled = arrivesSlides.length < 2;
    }
    if (dots instanceof HTMLElement) {
      dots.innerHTML = arrivesSlides
        .map((_, i) => {
          const active = i === arrivesIndex ? ' is-active' : '';
          return `<button type="button" class="amada-arrives-lightbox__dot${active}" data-amada-arrives-dot="${i}" aria-label="Image ${i + 1}"></button>`;
        })
        .join('');
    }
  }

  function openArrivesGallery(gallery, startIndex) {
    const buttons = [...gallery.querySelectorAll('[data-amada-arrives-open]')];
    arrivesSlides = buttons
      .map((btn) => {
        const img = btn.querySelector('img');
        if (!(img instanceof HTMLImageElement)) return null;
        return {
          src: img.dataset.fullSrc || img.currentSrc || img.src,
          caption: img.dataset.caption || img.alt || '',
        };
      })
      .filter(Boolean);

    if (!arrivesSlides.length) return;

    arrivesIndex = Math.max(0, Math.min(Number(startIndex) || 0, arrivesSlides.length - 1));
    arrivesLastFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const root = ensureArrivesLightbox();
    root.hidden = false;
    document.body.classList.add('amada-arrives-lightbox-open');
    paintArrivesSlide();
    root.querySelector('[data-amada-arrives-close]')?.focus?.();
  }

  function closeArrivesGallery() {
    if (!(arrivesLightbox instanceof HTMLElement) || arrivesLightbox.hidden) return;
    arrivesLightbox.hidden = true;
    document.body.classList.remove('amada-arrives-lightbox-open');
    if (arrivesLastFocus && arrivesLastFocus.isConnected) {
      arrivesLastFocus.focus();
    }
    arrivesLastFocus = null;
  }

  function stepArrivesGallery(delta) {
    if (!arrivesSlides.length) return;
    arrivesIndex = (arrivesIndex + delta + arrivesSlides.length) % arrivesSlides.length;
    paintArrivesSlide();
  }

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const openBtn = target.closest('[data-amada-arrives-open]');
    if (openBtn) {
      event.preventDefault();
      const gallery = openBtn.closest('[data-amada-arrives-gallery]');
      if (!(gallery instanceof HTMLElement)) return;
      openArrivesGallery(gallery, openBtn.dataset.index || 0);
      return;
    }

    if (target.closest('[data-amada-arrives-close]')) {
      event.preventDefault();
      closeArrivesGallery();
      return;
    }
    if (target.closest('[data-amada-arrives-prev]')) {
      event.preventDefault();
      stepArrivesGallery(-1);
      return;
    }
    if (target.closest('[data-amada-arrives-next]')) {
      event.preventDefault();
      stepArrivesGallery(1);
      return;
    }
    const dot = target.closest('[data-amada-arrives-dot]');
    if (dot instanceof HTMLElement) {
      event.preventDefault();
      arrivesIndex = Number(dot.getAttribute('data-amada-arrives-dot') || 0);
      paintArrivesSlide();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (!(arrivesLightbox instanceof HTMLElement) || arrivesLightbox.hidden) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      closeArrivesGallery();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      stepArrivesGallery(-1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      stepArrivesGallery(1);
    }
  });

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (!document.querySelector('.amada-cart')) return;

    const changeBtn = target.closest('[data-amada-cart-size-change]');
    if (changeBtn) {
      event.preventDefault();
      const root = changeBtn.closest('[data-amada-cart-size]');
      showSizeEdit(root);
      return;
    }

    const editEngraving = target.closest('[data-amada-engraving-edit]');
    if (editEngraving) {
      event.preventDefault();
      const root = engravingRoot(editEngraving);
      showEngravingCompose(root, { focus: true });
      return;
    }

    const removeEngraving = target.closest('[data-amada-engraving-remove]');
    if (removeEngraving) {
      event.preventDefault();
      const root = engravingRoot(removeEngraving);
      if (!(root instanceof HTMLElement) || root.classList.contains('is-removing')) return;
      const input = root.querySelector('input[data-amada-engraving]');
      if (!(input instanceof HTMLInputElement)) return;

      setEngravingRemoveLoading(root, true);
      updateLineProperty(input, '', { removeWhenBlank: true })
        .then(() => {
          if (!root.isConnected) return;
          input.value = '';
          const count = root.querySelector('[data-amada-engraving-count]');
          if (count instanceof HTMLElement) {
            const max = Number(input.dataset.maxLength || input.maxLength || 25);
            count.textContent = `0/${max}`;
          }
          setEngravingRemoveLoading(root, false);
          showEngravingCompose(root, { focus: false });
        })
        .catch(() => {
          if (root.isConnected) setEngravingRemoveLoading(root, false);
        });
      return;
    }

    const addEngraving = target.closest('[data-amada-engraving-add]');
    if (addEngraving) {
      event.preventDefault();
      const root = engravingRoot(addEngraving);
      submitEngraving(root);
    }
  });

  document.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLSelectElement)) return;
    if (!target.matches('[data-amada-ring-size]')) return;
    if (!document.querySelector('.amada-cart')) return;

    const value = target.value;
    const root = target.closest('[data-amada-cart-size]');

    // Optimistic UI: unknown → summary; letter size → keep edit (dropdown visible)
    if (root instanceof HTMLElement) {
      if (isUnknownSize(value)) {
        showSizeSummary(root, UNKNOWN_SIZE);
      } else if (value) {
        showSizeEdit(root);
      }
    }

    updateLineProperty(target, value || UNKNOWN_SIZE);
  });

  document.addEventListener('input', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (!target.matches('[data-amada-engraving]')) return;
    if (!document.querySelector('.amada-cart')) return;

    const max = Number(target.dataset.maxLength || target.maxLength || 25);
    const used = Math.min(String(target.value || '').length, max);
    const root = target.closest('[data-amada-cart-engraving]');
    const count = root?.querySelector('[data-amada-engraving-count]');
    if (count instanceof HTMLElement) {
      count.textContent = `${used}/${max}`;
    }
    syncEngravingAddVisibility(root);
  });

  document.addEventListener('keydown', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (!target.matches('[data-amada-engraving]')) return;
    if (!document.querySelector('.amada-cart')) return;
    if (event.key !== 'Enter') return;
    event.preventDefault();
    const root = engravingRoot(target);
    submitEngraving(root);
  });

  function updateNoteCount(textarea) {
    if (!(textarea instanceof HTMLTextAreaElement)) return;
    const field = textarea.closest('.cart-note__field');
    const count = field && field.querySelector('[data-amada-cart-note-count]');
    if (!(count instanceof HTMLElement)) return;
    const max = Number(textarea.getAttribute('maxlength') || 100);
    const len = Math.min(String(textarea.value || '').length, max);
    count.textContent = len + '/' + max + ' characters used';
  }

  document.addEventListener(
    'input',
    (event) => {
      const target = event.target;
      if (!(target instanceof HTMLTextAreaElement)) return;
      if (!target.matches('[data-amada-cart-note], #cart-note.cart-note__instructions')) return;
      if (!document.querySelector('.amada-cart')) return;
      updateNoteCount(target);
    },
    true
  );

  /**
   * After a full cart HTML paint, force saved engraving UI from input values
   * (covers first-load cases where the fee row is present but the nested block lagged).
   */
  function hydrateEngravingUi() {
    if (!document.querySelector('.amada-cart')) return;
    document.querySelectorAll('[data-amada-cart-engraving]').forEach((root) => {
      if (!(root instanceof HTMLElement)) return;
      const input = root.querySelector('input[data-amada-engraving]');
      if (!(input instanceof HTMLInputElement)) return;
      const value = String(input.value || '').trim();
      if (value) {
        showEngravingSaved(root, value);
      } else if (!root.classList.contains('is-saved')) {
        showEngravingCompose(root, { focus: false });
      }
    });
  }

  /**
   * On cart page load: drop orphan engraving fees and restore nested engraving UI.
   * Never full-page-reloads (that caused a continuous refresh loop on empty cart).
   */
  function bootCartEngraving() {
    if (!document.querySelector('.amada-cart')) return;
    hydrateEngravingUi();
    syncEngravingFee(null, { skipMorph: false }).finally(() => {
      hydrateEngravingUi();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootCartEngraving);
  } else {
    bootCartEngraving();
  }

  /**
   * The Back and Forward buttons bring back an old copy of the cart, including items the shopper already
   * removed. Removing one of those then leaves a blank row and stale totals. Load a fresh copy for that
   * case only. A reload is a different navigation type, so this cannot loop.
   */
  window.addEventListener('pageshow', (event) => {
    if (!document.querySelector('.amada-cart')) return;
    const navigation = performance.getEntriesByType('navigation')[0];
    if (event.persisted || (navigation && navigation.type === 'back_forward')) window.location.reload();
  });

}